'use client';

import type { Espece } from '@repo/types';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useCreateEspece } from '../../../hooks/api/especes/index';
import { useGetCurrentUser } from '../../../hooks/api/users/use-current-user';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';

interface AddEspeceQuickFormProps {
  onSuccess: (espece: Espece) => void;
  onCancel: () => void;
}

export function AddEspeceQuickForm({ onSuccess, onCancel }: AddEspeceQuickFormProps) {
  const [genre, setGenre]   = useState('');
  const [nomFr, setNomFr]   = useState('');
  const [errors, setErrors] = useState<{ genre?: string; nom_fr?: string }>({});

  const currentUser  = useGetCurrentUser();
  const userId       = (currentUser.data as any)?.data?.id as number | undefined;
  const createEspece = useCreateEspece();

  const validate = () => {
    const next: { genre?: string; nom_fr?: string } = {};
    if (!genre.trim()) next.genre  = 'Le genre est requis';
    if (!nomFr.trim()) next.nom_fr = 'Le nom français est requis';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const result = await createEspece.mutateAsync({
        name:          genre.trim(),
        genre:         genre.trim(),
        nom_fr:        nomFr.trim(),
        user_added_id: userId ?? null,
      } as any);
      onSuccess((result as any).data as Espece);
    } catch {
      // error handled via createEspece.isError
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-2">
      {createEspece.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {(createEspece.error as any)?.response?.data?.message ?? 'Erreur lors de la création.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Genre */}
      <div className="space-y-1.5">
        <Label htmlFor="aeqf-genre">Genre *</Label>
        <Input
          id="aeqf-genre"
          value={genre}
          onChange={(e) => {
            setGenre(e.target.value);
            if (errors.genre) setErrors((p) => ({ ...p, genre: undefined }));
          }}
          placeholder="ex : Quercus"
          className={errors.genre ? 'border-destructive' : ''}
          autoFocus
        />
        {errors.genre && (
          <p className="text-xs text-destructive">{errors.genre}</p>
        )}
      </div>

      {/* Nom français */}
      <div className="space-y-1.5">
        <Label htmlFor="aeqf-nom-fr">Nom français *</Label>
        <Input
          id="aeqf-nom-fr"
          value={nomFr}
          onChange={(e) => {
            setNomFr(e.target.value);
            if (errors.nom_fr) setErrors((p) => ({ ...p, nom_fr: undefined }));
          }}
          placeholder="ex : Chêne pédonculé"
          className={errors.nom_fr ? 'border-destructive' : ''}
        />
        {errors.nom_fr && (
          <p className="text-xs text-destructive">{errors.nom_fr}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={createEspece.isPending}
        >
          Annuler
        </Button>
        <Button type="submit" disabled={createEspece.isPending}>
          {createEspece.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Création…
            </>
          ) : (
            "Créer l'essence"
          )}
        </Button>
      </div>
    </form>
  );
}
